import type {AppRouteRecordRaw, Menu} from '/@/router/types';

import {defineStore} from 'pinia';
import {store} from '/@/store';
import {useUserStore} from './user';
import {useAppStoreWithOut} from './app';
import {toRaw} from 'vue';
import {flatMultiLevelRoutes, transformObjToRoute} from '/@/router/helper/routeHelper';
import {transformRouteToMenu} from '/@/router/helper/menuHelper';

import projectSetting from '/@/settings/projectSetting';

import {PermissionModeEnum} from '/@/enums/appEnum';

import {asyncRoutes} from '/@/router/routes';
import {ERROR_LOG_ROUTE, PAGE_NOT_FOUND_ROUTE} from '/@/router/routes/basic';

import {filter} from '/@/utils/helper/treeHelper';

import {getMenuList} from '/@/api/sys/menu';
import {getPermCode} from '/@/api/sys/user';
import {PageEnum} from '/@/enums/pageEnum';
import {RouterEnum} from '/@/enums/routerEnum';
import {headData} from '/@/router/routes/menu'

interface PermissionState {
    // Permission code list
    permCodeList: string[] | number[];
    // Whether the route has been dynamically added
    isDynamicAddedRoute: boolean;
    // To trigger a menu update
    lastBuildMenuTime: number;
    // Backstage menu list
    backMenuList: Menu[];
    frontMenuList: Menu[];
    /**
     * 唐鑫 2022-4-1
     * 扩展当前系统路由所属模块类型定义，用于支持路由按模块动态加载
     * 默认无模块时，加载所有路由，需要与router.meta配合使用
     */
    currentRouterType: RouterEnum;
    /**
     * 扩展兼容模式下的TOP_MENUS动态获取菜单列表
     */
    topMenuList: Menu[];
}

export const usePermissionStore = defineStore({
    id: 'app-permission',
    state: (): PermissionState => ({
        permCodeList: [],
        // Whether the route has been dynamically added
        isDynamicAddedRoute: false,
        // To trigger a menu update
        lastBuildMenuTime: 0,
        // Backstage menu list
        backMenuList: [],
        // menu List
        frontMenuList: [],
        /**
         * 唐鑫 2022-4-1
         * 扩展当前系统路由所属模块类型定义，用于支持路由按模块动态加载
         * 默认无模块时，加载所有路由，需要与router.meta配合使用
         */
        currentRouterType: RouterEnum.NONE,
        /**
         * 扩展兼容模式下的TOP_MENUS动态获取菜单列表
         */
        topMenuList: [],
    }),
    getters: {
        /**
         * 返回扩展兼容模式下的TOP_MENUS动态获取菜单列表
         * @returns 扩展兼容模式下的TOP_MENUS动态获取菜单列表
         */
        getTopMenuList(): Menu[] {
            return this.topMenuList;
        },
        /**
         * 返回当前路由所属的业务模块
         * @returns 当前路由所属业务模块
         */
        getCurrentRouterType(): RouterEnum {
            return this.currentRouterType;
        },
        getPermCodeList(): string[] | number[] {
            return this.permCodeList;
        },
        getBackMenuList(): Menu[] {
            return this.backMenuList;
        },
        getFrontMenuList(): Menu[] {
            return this.frontMenuList;
        },
        getLastBuildMenuTime(): number {
            return this.lastBuildMenuTime;
        },
        getIsDynamicAddedRoute(): boolean {
            return this.isDynamicAddedRoute;
        },
    },
    actions: {
        /**
         *  设置扩展兼容模式下的TOP_MENUS动态获取菜单列表
         * @param list 扩展兼容模式下的TOP_MENUS动态获取菜单列表
         */

        setTopMenuList(list: Menu[]) {
            this.topMenuList = list;
        },
        /**
         * 动态设置当前路由所属业务模块类型
         * @param routerType 当前路由所属业务模块类型
         */
        setCurrentRouterType(routerType: RouterEnum) {
            this.currentRouterType = routerType;
        },
        setPermCodeList(codeList: string[]) {
            this.permCodeList = codeList;
        },

        setBackMenuList(list: Menu[]) {
            this.backMenuList = list;
            list?.length > 0 && this.setLastBuildMenuTime();
        },

        setFrontMenuList(list: Menu[]) {
            this.frontMenuList = list;
        },

        setLastBuildMenuTime() {
            this.lastBuildMenuTime = new Date().getTime();
        },

        setDynamicAddedRoute(added: boolean) {
            this.isDynamicAddedRoute = added;
        },
        resetState(): void {
            this.isDynamicAddedRoute = false;
            this.permCodeList = [];
            this.backMenuList = [];
            this.lastBuildMenuTime = 0;
        },
        async changePermissionCode() {
            const codeList = await getPermCode();
            this.setPermCodeList(codeList);
        },
        // 获取公司信息
        // async changePermissionTalents() {
        //     const userStore = useUserStore();
        //     const userData = userStore.getUserInfo;
        //     const tanlentsList = await getTalents();
        //     userData.tanlentsList = tanlentsList[0];
        //     userData.siteInfo = await getSiteInfo()
        //     userStore.setUserInfo(userData);
        // },
        // 前端模式过滤路由
        fnFilter(nameArr, routeArr) {
            return routeArr.filter(item => nameArr.includes(item.name)).map(el => {
                el = Object.assign({}, el)
                if (el.children && el.children.length) {
                    el.children = this.fnFilter(nameArr, el.children)
                }
                return el
            })
        },
        // 后端模式过滤路由
        backEndFilter(data) {
            return data.map(el => {
                el = Object.assign({}, el)
                if (!el.name) {
                    el.name = el.code
                }
                if (typeof (el.meta) == 'string') {
                    el.meta = eval('(' + el.meta + ')')
                }
                // if(el.checked){
                //     el.meta.authorization = true
                // }
                el.meta.code = el.code
                el.meta.authorization = true
                if (el.children && el.children.length > 0) {
                    el.children = this.backEndFilter(el.children)
                }
                // 排序
                if (el.children && el.children.length > 0) {
                    el.children.sort((a, b) => {
                        return (a.meta?.orderNo || 0) - (b.meta?.orderNo || 0);
                    });
                }
                return el
            })
        },
        async buildRoutesAction(): Promise<AppRouteRecordRaw[]> {
            const userStore = useUserStore();
            const appStore = useAppStoreWithOut();

            let routes: AppRouteRecordRaw[] = [];
            let homePath = ''
            const roleList = toRaw(userStore.getRoleList) || [];
            const {permissionMode = projectSetting.permissionMode} = appStore.getProjectConfig;

            const routeFilter = (route: AppRouteRecordRaw) => {
                const {meta} = route;
                const {roles} = meta || {};
                if (!roles) return true;
                return roleList.some((role) => roles.includes(role));
            };

            const routeRemoveIgnoreFilter = (route: AppRouteRecordRaw) => {
                const {meta} = route;
                const {ignoreRoute} = meta || {};
                return !ignoreRoute;
            };

            /**
             * @description 根据设置的首页path，修正routes中的affix标记（固定首页）
             * */
            const patchHomeAffix = (routes: AppRouteRecordRaw[], homeRoute) => {
                if (!routes || routes.length === 0) return;
                // let homePath: string = userStore.getUserInfo.homePath || PageEnum.BASE_HOME;
                let homePath: string = homeRoute || PageEnum.BASE_HOME;

                function patcher(routes: AppRouteRecordRaw[], parentPath = '') {
                    if (parentPath) parentPath = parentPath + '/';
                    routes.forEach((route: AppRouteRecordRaw) => {
                        const {path, children, redirect} = route;
                        const currentPath = path.startsWith('/') ? path : parentPath + path;
                        if (currentPath === homePath) {
                            if (redirect) {
                                homePath = route.redirect! as string;
                            } else {
                                route.meta = Object.assign({}, route.meta, {affix: true});
                                throw new Error('end');
                            }
                        }
                        children && children.length > 0 && patcher(children, currentPath);
                    });
                }

                try {
                    patcher(routes, homePath);
                } catch (e) {
                    // 已处理完毕跳出循环
                }
                return;
            };


            switch (permissionMode) {
                case PermissionModeEnum.ROLE:
                    routes = filter(asyncRoutes, routeFilter);
                    routes = routes.filter(routeFilter);
                    // Convert multi-level routing to level 2 routing
                    routes = flatMultiLevelRoutes(routes);
                    break;

                case PermissionModeEnum.ROUTE_MAPPING:
                    let nameArr: any = []
                    try {
                        this.changePermissionCode();
                        nameArr = (await getMenuList()) as AppRouteRecordRaw[];
                    } catch (error) {
                        console.error(error);
                    }
                    routes = filter(asyncRoutes, routeFilter);
                    routes = routes.filter(routeFilter);

                    // 过滤路由
                    // let filterRoutes = this.fnFilter(nameArr,routes)
                    let filterRoutes = routes

                    const menuList = transformRouteToMenu(filterRoutes, true);
                    filterRoutes = filter(filterRoutes, routeRemoveIgnoreFilter);
                    filterRoutes = filterRoutes.filter(routeRemoveIgnoreFilter);

                    menuList.sort((a, b) => {
                        return (a.meta?.orderNo || 0) - (b.meta?.orderNo || 0);
                    });
                    this.setFrontMenuList(menuList.filter((item) => item.meta && !item.meta.showInTopMenu));

                    /**
                     * 兼容逻辑处理从所有生成的路由菜单中获取meta.showInTopMenu=true的子项
                     */
                    this.setTopMenuList(menuList.filter((item) => item.meta && item.meta.showInTopMenu));

                    // Convert multi-level routing to level 2 routing
                    routes = flatMultiLevelRoutes(filterRoutes);
                    break;

                //  If you are sure that you do not need to do background dynamic permissions, please comment the entire judgment below
                case PermissionModeEnum.BACK:
                    // !Simulate to obtain permission codes from the background,
                    // this function may only need to be executed once, and the actual project can be put at the right time by itself
                    let routeList: any = [];
                    let leftRouteList: any = []

                    // 左侧菜单处理-----------------------------------------------
                    // let leftMenus = this.backEndFilter(leftRouteList[0].children)
                    let leftMenus = []

                    // 头部菜单处理--------------------------------------
                    let topMenus = headData
                    topMenus = this.backEndFilter(topMenus)
                    topMenus = transformObjToRoute(topMenus);
                    const menuList2 = transformRouteToMenu(topMenus, true);
                    topMenus = filter(topMenus, routeRemoveIgnoreFilter);
                    topMenus = topMenus.filter(routeRemoveIgnoreFilter);

                    menuList2.sort((a, b) => {
                        return (a.meta?.orderNo || 0) - (b.meta?.orderNo || 0);
                    });
                    this.setFrontMenuList(menuList2.filter((item) => item.meta && !item.meta.showInTopMenu));

                    /**
                     * 兼容逻辑处理从所有生成的路由菜单中获取meta.showInTopMenu=true的子项
                     */
                    this.setTopMenuList(menuList2.filter((item) => item.meta && item.meta.showInTopMenu));

                    // Convert multi-level routing to level 2 routing
                    topMenus = flatMultiLevelRoutes(topMenus);

                    let isLeftAuthorization = true
                    if (topMenus.length > 0) {
                        for (let i = 0; i < topMenus.length; i++) {
                            if (topMenus[i].meta.authorization) {
                                isLeftAuthorization = false
                                homePath = topMenus[i].path
                                break
                            }
                        }
                    }
                    if (isLeftAuthorization) {
                        homePath = '/homePage'
                    }

                    const userData = userStore.getUserInfo;
                    userData.homePath = homePath;
                    userStore.setUserInfo(userData);

                    routes = [PAGE_NOT_FOUND_ROUTE, ...leftMenus, ...topMenus];
                    break;
            }
            routes.push(ERROR_LOG_ROUTE);
            patchHomeAffix(routes, homePath);
            return routes;
        },
    },
});

// Need to be used outside the setup
export function usePermissionStoreWithOut() {
    return usePermissionStore(store);
}
